import React from 'react';
import {
  ArrowSquareOutIcon,
  BookOpenIcon,
  CertificateIcon,
  FileTextIcon,
  IdentificationCardIcon,
  InfoIcon,
  QuestionIcon,
  ShieldCheckIcon,
  type Icon as PhosphorIcon,
} from '@phosphor-icons/react';
import { withBlockExtensions } from '@plone/volto/helpers';
import {
  MPFButton,
  MPFCardCarousel,
  MPFLink,
} from 'volto-ministerio-publico-federal/components/UI';
import { EditItemButton } from 'volto-ministerio-publico-federal/components/View/CapaView/EditItemButton';

import {
  GUIA_EXTERNO_HINT,
  GUIAS_MAX_ITENS,
  GUIAS_TITULO,
  guiaIsCompleto,
  toGuiaCards,
  type GuiaCard,
  type GuiaIcone,
  type GuiasData,
} from './helpers';

interface ViewProps {
  data: GuiasData;
  isEditMode?: boolean;
  onEditItem?: (index: number) => void;
}

const ICONES: Record<GuiaIcone, PhosphorIcon> = {
  IdentificationCard: IdentificationCardIcon,
  FileText: FileTextIcon,
  BookOpen: BookOpenIcon,
  Certificate: CertificateIcon,
  Info: InfoIcon,
  Question: QuestionIcon,
  ShieldCheck: ShieldCheckIcon,
};

const ROOT_CLASS = 'mpf-guias not-prose';

const CARROSSEL_ROTULOS = {
  prev: 'Guia anterior',
  next: 'Próximo guia',
  first: 'Este é o primeiro guia',
  last: 'Este é o último guia',
  bullet: 'Ir para o guia {{index}}',
};

const Aviso: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="mpf-guias__aviso">{children}</p>
);

export const MpfGuiasBlockView: React.FC<ViewProps> = ({
  data,
  isEditMode,
  onEditItem,
}) => {
  const tituloId = React.useId();
  const cards = React.useMemo(() => toGuiaCards(data?.itens), [data?.itens]);
  const visiveis = isEditMode ? cards : cards.filter(guiaIsCompleto);
  const excedentes = (data?.itens?.length ?? 0) - GUIAS_MAX_ITENS;
  const titulo = data?.titulo?.trim() || GUIAS_TITULO;

  const cabecalho = (
    <h2 id={tituloId} className="mpf-guias__titulo">
      {titulo}
    </h2>
  );

  if (visiveis.length === 0) {
    return isEditMode ? (
      <section className={ROOT_CLASS}>
        {cabecalho}
        <Aviso>Escolha os guias da faixa na barra lateral.</Aviso>
      </section>
    ) : null;
  }

  const comCarrossel = visiveis.length > 1;
  const carrosselNoDesktop = !isEditMode && visiveis.length > 3;
  const listaClasses = [
    'mpf-guias__lista',
    visiveis.length < 3 && `mpf-guias__lista--${visiveis.length}`,
    comCarrossel && 'mpf-guias__lista--desktop',
  ]
    .filter(Boolean)
    .join(' ');

  const conteudo = (card: GuiaCard) => (
    <>
      <Guia card={card} isEditMode={isEditMode} />
      {onEditItem && (
        <EditItemButton
          label={`Editar guia: ${card.titulo || 'sem título'}`}
          onClick={() => onEditItem(card.sourceIndex)}
          className="absolute right-2 top-2"
        />
      )}
    </>
  );

  return (
    <section className={ROOT_CLASS} aria-labelledby={tituloId}>
      {cabecalho}

      {!carrosselNoDesktop && (
        <ul className={listaClasses}>
          {visiveis.map((card) => (
            <li key={card.key} className="mpf-guias__item">
              {conteudo(card)}
            </li>
          ))}
        </ul>
      )}

      {comCarrossel && (
        <MPFCardCarousel
          key={visiveis.map((card) => card.key).join('|')}
          name="guias"
          className={
            carrosselNoDesktop
              ? 'mpf-guias__carrossel mpf-guias__carrossel--desktop'
              : 'mpf-guias__carrossel'
          }
          slides={visiveis.map((card) => ({
            key: card.key,
            content: <div className="mpf-guias__slide">{conteudo(card)}</div>,
          }))}
          slidesPerView={1.08}
          spaceBetween={8}
          breakpoints={{
            768: { slidesPerView: 2.05, spaceBetween: 8 },
            1024: { slidesPerView: 3, spaceBetween: 32 },
          }}
          showArrows={false}
          labels={CARROSSEL_ROTULOS}
        />
      )}

      {isEditMode && excedentes > 0 && (
        <Aviso>
          {excedentes === 1
            ? `O último guia não aparece: a faixa exibe no máximo ${GUIAS_MAX_ITENS}.`
            : `Os últimos ${excedentes} guias não aparecem: a faixa exibe no máximo ${GUIAS_MAX_ITENS}.`}
        </Aviso>
      )}
    </section>
  );
};

const Guia: React.FC<{ card: GuiaCard; isEditMode?: boolean }> = ({
  card,
  isEditMode,
}) => {
  const Icone = ICONES[card.icone];
  const oculto = isEditMode && !guiaIsCompleto(card);
  const conteudoBotao = (
    <>
      {card.botaoRotulo}
      {card.externo && (
        <>
          <ArrowSquareOutIcon aria-hidden="true" />
          <span className="sr-only">{GUIA_EXTERNO_HINT}</span>
        </>
      )}
    </>
  );

  return (
    <article
      className={
        oculto ? 'mpf-guias__card mpf-guias__card--oculto' : 'mpf-guias__card'
      }
    >
      <div className="mpf-guias__topo">
        <span className="mpf-guias__icone" aria-hidden="true">
          <Icone />
        </span>
        {card.selo && <p className="mpf-guias__selo">{card.selo}</p>}
      </div>

      <div className="mpf-guias__corpo">
        <h3 className="mpf-guias__card-titulo">
          {card.titulo || 'Guia sem título'}
        </h3>
        {card.descricao && (
          <p className="mpf-guias__descricao">{card.descricao}</p>
        )}
      </div>

      {oculto && (
        <p className="mpf-guias__oculto">
          {card.titulo
            ? 'Não aparece no site: falta o link de destino.'
            : 'Não aparece no site: falta o título.'}
        </p>
      )}

      {card.href && !isEditMode ? (
        <MPFButton
          as={MPFLink}
          href={card.href}
          variant="outline"
          size="m"
          className="mpf-guias__botao"
        >
          {conteudoBotao}
        </MPFButton>
      ) : (
        <MPFButton
          as="span"
          variant="outline"
          size="m"
          className="mpf-guias__botao"
        >
          {conteudoBotao}
        </MPFButton>
      )}
    </article>
  );
};

export default withBlockExtensions(MpfGuiasBlockView);
